import { ChangeDetectorRef, Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { NgGlyph } from "@ng-icons/core";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AppAvatarComponent } from "../../../../Common/Components/Avatar/AvatarComponent";
import { UserListItem } from "../../Dto/UserModel";
import { UserEffects } from "../../State/UserEffect";
import { FileApi } from "../../../File/Api/FileApi";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { RoleListItem } from "../../../Role/Dto/RoleModel";
import { RoleEffects } from "../../../Role/State/RoleEffect";
import { RoleState } from "../../../Role/State/RoleState";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-user-edit",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, AppAvatarComponent, NgGlyph],
  templateUrl: "./UserEditPage.html",
  styleUrl: "./UserEditPage.scss",
})
export class UserEditPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userEffects = inject(UserEffects);
  private readonly fileApi = inject(FileApi);
  private readonly toastEffects = inject(ToastEffects);
  private readonly roleEffects = inject(RoleEffects);
  private readonly roleState = inject(RoleState);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly userId = this.route.snapshot.paramMap.get("id") ?? undefined;

  profileForm: FormGroup;
  passwordForm: FormGroup;

  user: UserListItem | null = null;
  isReady = false;
  roles: RoleListItem[] = [];
  isSaving = false;
  isPasswordSaving = false;
  isProfileSaveDisabled = true;
  isPasswordSubmitDisabled = true;

  constructor() {
    this.profileForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      email: ["", [Validators.required, Validators.email]],
      phone: [""],
      address: [""],
      role: ["", [Validators.required]],
      imageUrl: [""],
    });
    this.passwordForm = this.fb.group(
      {
        newPassword: ["", [Validators.required, Validators.minLength(8)]],
        confirmPassword: ["", [Validators.required, Validators.minLength(8)]],
      },
      { validators: passwordMatchValidator },
    );

    this.profileForm.statusChanges.subscribe(() => {
      this.syncActionState();
    });

    this.passwordForm.statusChanges.subscribe(() => {
      this.syncActionState();
    });
  }

  async ngOnInit(): Promise<void> {
    if (!this.userId) {
      await this.router.navigate(["/users"]);
      return;
    }

    await this.loadRoles();
    await this.fetchUserProfile();
  }

  private async loadRoles(): Promise<void> {
    try {
      await this.roleEffects.searchRoles();
      this.roles = this.roleState.data();
    } catch (exception) {
      console.error("Error loading roles:", exception);
    }
  }

  private async fetchUserProfile(): Promise<void> {
    if (!this.userId) {
      this.toastEffects.show("User ID is required.", "error");
      await this.router.navigate(["/users"]);
      return;
    }

    const profile = await this.userEffects.getManageProfile(this.userId);
    if (!profile) {
      this.toastEffects.show("User not found.", "error");
      await this.router.navigate(["/users"]);
      return;
    }

    setTimeout(() => {
      this.user = profile;
      this.hydrateProfileForm(profile);
      this.isReady = true;
      this.syncActionState();
      this.cdr.detectChanges();
    }, 0);
  }

  async saveUser(): Promise<void> {
    this.logInvalidProfileFields();
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    if (!this.userId) {
      this.toastEffects.show("Missing user identifier.", "error");
      return;
    }

    this.isSaving = true;
    this.syncActionState();
    this.cdr.detectChanges();
    const formData = this.profileForm.value;

    const updateData: Partial<UserListItem> = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone ?? "",
      address: formData.address ?? "",
      role: formData.role,
    };

    const imageUrl = (() => {
      if (typeof formData.imageUrl === "string") {
        return formData.imageUrl.trim();
      }
      return "";
    })();
    if (imageUrl) {
      updateData.imageUrl = imageUrl;
    }

    try {
      const updatedUser = await this.userEffects.updateManageProfile(this.userId, updateData);

      setTimeout(() => {
        this.user = updatedUser;
        this.hydrateProfileForm(updatedUser);
        this.syncActionState();
        this.toastEffects.show("User profile updated", "success");
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to save user."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.syncActionState();
        this.cdr.detectChanges();
      }, 0);
    }
  }

  async onChangePassword(): Promise<void> {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    if (!this.userId) {
      this.toastEffects.show("Missing user identifier.", "error");
      return;
    }

    const { newPassword } = this.passwordForm.value;
    if (!newPassword) {
      return;
    }

    this.isPasswordSaving = true;
    this.syncActionState();
    this.cdr.detectChanges();
    try {
      const result = await this.userEffects.resetPasswordByAdmin(this.userId, { newPassword });
      if (result.success) {
        setTimeout(() => {
          this.toastEffects.show(result.message, "success");
          this.passwordForm.reset();
          this.passwordForm.markAsPristine();
          this.passwordForm.markAsUntouched();
          this.syncActionState();
          this.cdr.detectChanges();
        }, 0);
      } else {
        this.toastEffects.show(result.message, "error");
      }
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Failed to update password"), "error");
    } finally {
      setTimeout(() => {
        this.isPasswordSaving = false;
        this.syncActionState();
        this.cdr.detectChanges();
      }, 0);
    }
  }

  async onAvatarChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    if (!this.userId) {
      this.toastEffects.show("Missing user identifier.", "error");
      return;
    }

    const file = input.files[0];

    try {
      const uploadedFile = await this.fileApi.uploadFile(file, { folder: "users" });
      const imageUrl = uploadedFile.publicUrl;
      const updatedUser = await this.userEffects.updateManageProfile(this.userId, { imageUrl });

      setTimeout(() => {
        this.user = updatedUser;
        this.hydrateProfileForm(updatedUser);
        this.syncActionState();
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Failed to update image"), "error");
    } finally {
      input.value = "";
    }
  }

  async removeAvatar(): Promise<void> {
    if (!this.userId || !this.user?.imageUrl) {
      return;
    }

    try {
      const updatedUser = await this.userEffects.updateManageProfile(this.userId, { imageUrl: null });

      setTimeout(() => {
        this.user = updatedUser;
        this.hydrateProfileForm(updatedUser);
        this.syncActionState();
        this.cdr.detectChanges();
      }, 0);
      this.toastEffects.show("Image removed", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Failed to remove image"), "error");
    }
  }

  goBack(): void {
    this.router.navigate(["/users"]).catch(() => undefined);
  }

  private hydrateProfileForm(user: UserListItem): void {
    this.profileForm.patchValue(
      {
        name: user.name,
        email: user.email,
        phone: user.phone ?? "",
        address: user.address ?? "",
        role: user.role?.id ?? "",
        imageUrl: user.imageUrl ?? "",
      },
      { emitEvent: false },
    );
    this.profileForm.markAsPristine();
    this.profileForm.markAsUntouched();
  }

  private syncActionState(): void {
    this.isProfileSaveDisabled = this.isSaving || this.profileForm.invalid || !this.userId || !this.isReady;
    this.isPasswordSubmitDisabled = this.passwordForm.invalid || this.isPasswordSaving;
  }

  private logInvalidProfileFields(): void {
    Object.entries(this.profileForm.controls)
      .filter(([, control]) => control.invalid)
      .map(([key, control]) => ({
        key,
        value: control.value,
        errors: control.errors,
      }));
  }
}

function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const current = control.get("newPassword")?.value;
  const confirm = control.get("confirmPassword")?.value;
  if (!current || !confirm) {
    return null;
  }
  return (() => {
    if (current === confirm) {
      return null;
    }
    return { passwordMismatch: true };
  })();
}
